/**
 * Buscador valorizado.
 *
 * Responde con cantidad, precio, recargos y total: «todos los G30», «cuánto
 * compró PROMET», «qué consumió el centro de costo AM-059». El factor Codelco
 * de sobrestadía va desglosado aparte, porque es lo que el Excel dejaba fuera.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FileSpreadsheet, Search, SlidersHorizontal, X } from 'lucide-react';
import {
  Boton,
  Campo,
  Chip,
  Indicador,
  Selector,
  Tarjeta,
  TarjetaTitulo,
  Vacio,
} from '@/components/ui';
import { useGuiasValorizadas } from './useAnalisis';
import {
  DIMENSIONES,
  NOMBRE_RECARGO,
  agrupar,
  filtrar,
  opcionesDe,
  type Dimension,
  type FiltroAnalisis,
} from '@/domain/analisis';
import { clp, fecha as ffecha, m3 as fm3, num, uf } from '@/domain/formato';
import { formatearMinutos } from '@/domain/tiempos';
import { cn } from '@/lib/cn';

/** Selector de varias opciones, compacto. */
function MultiSelector({
  etiqueta,
  opciones,
  valor,
  alCambiar,
  formato,
}: {
  etiqueta: string;
  opciones: (string | number)[];
  valor: (string | number)[];
  alCambiar: (v: string[]) => void;
  formato?: (v: string | number) => string;
}) {
  if (opciones.length === 0) return null;
  return (
    <div>
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-texto-mute">
        {etiqueta}
        {valor.length > 0 && <span className="ml-1 text-fluor-verde">({valor.length})</span>}
      </span>
      <select
        multiple
        value={valor.map(String)}
        onChange={(e) => alCambiar([...e.target.selectedOptions].map((o) => o.value))}
        size={Math.min(5, Math.max(3, opciones.length))}
        className="w-full rounded-control border border-linea bg-petroleo-800/60 px-2 py-1.5 text-xs text-texto focus:border-petroleo-300 focus:outline-none"
      >
        {opciones.map((o) => (
          <option key={String(o)} value={String(o)} className="py-0.5">
            {formato ? formato(o) : String(o)}
          </option>
        ))}
      </select>
    </div>
  );
}

const VACIO: FiltroAnalisis = {};

export default function Buscador() {
  const { valorizadas, cargando } = useGuiasValorizadas();
  const [filtro, setFiltro] = useState<FiltroAnalisis>(VACIO);
  const [dimension, setDimension] = useState<Dimension>('producto');
  const [valorUf, setValorUf] = useState('40860,6');
  const [abiertos, setAbiertos] = useState(true);
  const [expandida, setExpandida] = useState<string | null>(null);

  const ufNum = Number(valorUf.replace(/\./g, '').replace(',', '.')) || 0;
  const set = <K extends keyof FiltroAnalisis>(k: K, v: FiltroAnalisis[K]) =>
    setFiltro((p) => ({ ...p, [k]: v }));

  const opciones = useMemo(() => opcionesDe(valorizadas), [valorizadas]);
  const filtradas = useMemo(() => filtrar(valorizadas, filtro), [valorizadas, filtro]);
  const resultado = useMemo(
    () => agrupar(filtradas, dimension, ufNum),
    [filtradas, dimension, ufNum],
  );

  const activos = Object.entries(filtro).filter(
    ([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
  ).length;

  const t = resultado.totales;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Buscador</h1>
          <p className="text-xs text-texto-mute">
            {cargando
              ? 'Cargando…'
              : `${num(filtradas.length)} de ${num(valorizadas.length)} guías`}
          </p>
        </div>
        <div className="flex gap-2">
          <Boton
            variante="secundario"
            icono={<SlidersHorizontal size={15} />}
            onClick={() => setAbiertos((v) => !v)}
          >
            Filtros{activos > 0 ? ` (${activos})` : ''}
          </Boton>
          <Boton
            variante="secundario"
            icono={<FileSpreadsheet size={15} />}
            onClick={() =>
              void import('@/db/exportarExcel').then((m) =>
                m.exportarAnalisis(
                  DIMENSIONES.find((d) => d.id === dimension)!.texto,
                  resultado.filas,
                  resultado.totales,
                  filtradas,
                ),
              )
            }
            disabled={!resultado.filas.length}
          >
            Exportar a Excel
          </Boton>
        </div>
      </div>

      {/* Búsqueda rápida */}
      <Tarjeta>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Campo
            etiqueta="Buscar"
            name="qTexto"
            placeholder="G30, PROMET, AM-059, folio…"
            value={filtro.texto ?? ''}
            onChange={(e) => set('texto', e.target.value)}
            className="lg:col-span-2"
            sufijo={<Search size={14} />}
          />
          <Campo
            etiqueta="Desde"
            name="qDesde"
            type="date"
            value={filtro.desde ?? ''}
            onChange={(e) => set('desde', e.target.value || undefined)}
          />
          <Campo
            etiqueta="Hasta"
            name="qHasta"
            type="date"
            value={filtro.hasta ?? ''}
            onChange={(e) => set('hasta', e.target.value || undefined)}
          />
          <Campo
            etiqueta="Valor UF"
            name="qUf"
            inputMode="decimal"
            sufijo="$"
            value={valorUf}
            onChange={(e) => setValorUf(e.target.value)}
            className="font-mono"
          />
        </div>

        {abiertos && (
          <div className="grid gap-3 border-t border-linea p-4 sm:grid-cols-2 lg:grid-cols-4">
            <MultiSelector
              etiqueta="Resistencia"
              opciones={opciones.mpa}
              valor={filtro.mpa ?? []}
              alCambiar={(v) => set('mpa', v.map(Number))}
              formato={(v) => `G${v}`}
            />
            <MultiSelector
              etiqueta="Grado"
              opciones={opciones.grados}
              valor={filtro.grados ?? []}
              alCambiar={(v) => set('grados', v)}
            />
            <MultiSelector
              etiqueta="Extras"
              opciones={opciones.extras}
              valor={filtro.extras ?? []}
              alCambiar={(v) => set('extras', v)}
            />
            <MultiSelector
              etiqueta="Planta"
              opciones={opciones.plantas}
              valor={filtro.plantas ?? []}
              alCambiar={(v) => set('plantas', v)}
            />
            <MultiSelector
              etiqueta="Cliente"
              opciones={opciones.clientes}
              valor={filtro.clientes ?? []}
              alCambiar={(v) => set('clientes', v)}
            />
            <MultiSelector
              etiqueta="Centro de costo"
              opciones={opciones.centrosCosto}
              valor={filtro.centrosCosto ?? []}
              alCambiar={(v) => set('centrosCosto', v)}
            />
            <MultiSelector
              etiqueta="Producto"
              opciones={opciones.productos}
              valor={filtro.productos ?? []}
              alCambiar={(v) => set('productos', v)}
            />
            <MultiSelector
              etiqueta="Turno"
              opciones={opciones.turnos}
              valor={filtro.turnos ?? []}
              alCambiar={(v) => set('turnos', v)}
            />

            <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-4">
              {(
                [
                  ['soloConSobrestadia', 'Sólo con sobrestadía'],
                  ['soloIncompletas', 'Sólo sin horarios completos'],
                  ['incluirAnuladas', 'Incluir anuladas'],
                ] as const
              ).map(([k, texto]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set(k, filtro[k] ? undefined : true)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs transition-colors',
                    filtro[k]
                      ? 'border-fluor-verde/40 bg-fluor-verde/10 text-fluor-verde'
                      : 'border-linea text-texto-mute hover:text-texto',
                  )}
                >
                  {texto}
                </button>
              ))}
              {activos > 0 && (
                <Boton
                  variante="fantasma"
                  tamano="sm"
                  icono={<X size={13} />}
                  onClick={() => setFiltro(VACIO)}
                  className="ml-auto"
                >
                  Limpiar filtros
                </Boton>
              )}
            </div>
          </div>
        )}
      </Tarjeta>

      {/* Totales */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Indicador etiqueta="Viajes" valor={num(t.viajes)} />
        <Indicador etiqueta="Cantidad" valor={fm3(t.cantidadM3)} unidad="m³" />
        <Indicador
          etiqueta="Sobrestadía"
          valor={num(t.bloquesCodelco)}
          unidad="bloques"
          nivel={t.bloquesCodelco > 0 ? 'AMARILLO' : 'NEUTRO'}
          detalle={`${uf(t.montoRecargosUf, 2)} UF en recargos`}
        />
        <Indicador etiqueta="Total" valor={uf(t.totalUf, 2)} unidad="UF" nivel="VERDE" />
        <Indicador etiqueta="En pesos" valor={clp(t.totalClp)} nivel="VERDE" />
      </div>

      {/* Agrupación */}
      <Tarjeta>
        <TarjetaTitulo
          accion={
            <Selector
              name="dimension"
              value={dimension}
              onChange={(e) => setDimension(e.target.value as Dimension)}
              className="min-h-0 h-8 py-0 text-xs"
            >
              {DIMENSIONES.map((d) => (
                <option key={d.id} value={d.id}>
                  Agrupar por {d.texto.toLowerCase()}
                </option>
              ))}
            </Selector>
          }
        >
          Resultados
        </TarjetaTitulo>

        {cargando ? (
          <Vacio titulo="Cargando…" />
        ) : resultado.filas.length === 0 ? (
          <Vacio icono={<Search size={30} />} titulo="Sin resultados">
            Ajusta los filtros o el rango de fechas.
          </Vacio>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem] text-xs">
              <thead>
                <tr className="border-b border-linea text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">
                    {DIMENSIONES.find((d) => d.id === dimension)!.texto}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">Viajes</th>
                  <th className="px-3 py-2 text-right font-medium">m³</th>
                  <th className="px-3 py-2 text-right font-medium">Precio UF</th>
                  <th className="px-3 py-2 text-right font-medium">Monto UF</th>
                  <th className="px-3 py-2 text-right font-medium">Recargos UF</th>
                  <th className="px-3 py-2 text-right font-medium">Total UF</th>
                  <th className="px-3 py-2 text-right font-medium">Total $</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {resultado.filas.map((f) => {
                  const abierta = expandida === f.clave;
                  return (
                    <>
                      <tr
                        key={f.clave}
                        onClick={() => setExpandida(abierta ? null : f.clave)}
                        className="cursor-pointer border-b border-linea/50 hover:bg-white/[.03]"
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium">{f.clave}</div>
                          {f.detalle && (
                            <div className="max-w-[20rem] truncate text-[11px] text-texto-mute">
                              {f.detalle}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{num(f.viajes)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fm3(f.cantidadM3)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-texto-mute">
                          {uf(f.precioUfPromedio)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {uf(f.montoPrincipalUf, 2)}
                        </td>
                        <td
                          className={cn(
                            'px-3 py-2 text-right font-mono tabular-nums',
                            f.montoRecargosUf > 0 && 'text-fluor-amarillo',
                          )}
                        >
                          {uf(f.montoRecargosUf, 2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums">
                          {uf(f.totalUf, 2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-texto-mute">
                          {clp(f.totalClp)}
                        </td>
                        <td className="pr-2 text-texto-mute">
                          <ChevronRight
                            size={13}
                            className={cn('transition-transform', abierta && 'rotate-90')}
                          />
                        </td>
                      </tr>

                      {abierta && f.recargos.length > 0 && (
                        <tr key={f.clave + '-det'} className="border-b border-linea/50 bg-petroleo-900/40">
                          <td colSpan={9} className="px-4 py-3">
                            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-texto-mute">
                              Desglose de recargos
                            </div>
                            <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                              {f.recargos.map((r) => (
                                <div
                                  key={r.regla}
                                  className="flex items-center justify-between gap-2 rounded-control bg-petroleo-800/50 px-2.5 py-1.5"
                                >
                                  <span className="min-w-0 flex-1 truncate">
                                    <span className="font-mono text-texto-mute">{r.itemCodigo}</span>{' '}
                                    {NOMBRE_RECARGO[r.regla]}
                                  </span>
                                  <span className="shrink-0 font-mono tabular-nums text-texto-mute">
                                    {num(r.cantidad, r.cantidad % 1 === 0 ? 0 : 2)}
                                  </span>
                                  <span className="shrink-0 font-mono tabular-nums text-fluor-amarillo">
                                    {uf(r.montoUf, 2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {f.incompletas > 0 && (
                              <p className="mt-2 text-[11px] text-fluor-amarillo">
                                {num(f.incompletas)} guía(s) sin los 6 horarios: no se les pudo
                                calcular sobrestadía.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-petroleo-900/60 font-semibold">
                  <td className="px-4 py-2.5">TOTAL</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{num(t.viajes)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fm3(t.cantidadM3)}</td>
                  <td />
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                    {uf(t.montoPrincipalUf, 2)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-fluor-amarillo">
                    {uf(t.montoRecargosUf, 2)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-fluor-verde">
                    {uf(t.totalUf, 2)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-fluor-verde">
                    {clp(t.totalClp)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Tarjeta>

      {/* Guías del resultado */}
      {filtradas.length > 0 && (
        <Tarjeta>
          <TarjetaTitulo>
            Guías ({num(filtradas.length)}
            {filtradas.length > 100 ? ' — se muestran las 100 primeras' : ''})
          </TarjetaTitulo>
          <div className="max-h-96 overflow-auto">
            <table className="w-full min-w-[46rem] text-xs">
              <thead className="sticky top-0 bg-petroleo-700">
                <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                  <th className="px-4 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Folio</th>
                  <th className="px-3 py-2 font-medium">Cliente</th>
                  <th className="px-3 py-2 font-medium">Producto</th>
                  <th className="px-3 py-2 text-right font-medium">m³</th>
                  <th className="px-3 py-2 text-right font-medium">Estadía</th>
                  <th className="px-3 py-2 text-right font-medium">Bloques</th>
                  <th className="px-3 py-2 text-right font-medium">Total UF</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.slice(0, 100).map((g) => (
                  <tr key={g.id} className="border-t border-linea/40 hover:bg-white/[.03]">
                    <td className="whitespace-nowrap px-4 py-1.5">{ffecha(g.fecha)}</td>
                    <td className="px-3 py-1.5">
                      <Link to={`/guias/${g.id}`} className="font-mono text-petroleo-300 hover:underline">
                        {g.folio}
                      </Link>
                    </td>
                    <td className="max-w-[9rem] truncate px-3 py-1.5">{g.clienteNombre}</td>
                    <td className="max-w-[11rem] truncate px-3 py-1.5 font-mono">{g.productoCodigo}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fm3(g.cantidadM3)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-texto-mute">
                      {formatearMinutos(g.minEstadiaObra)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                      {g.bloquesCodelco ? (
                        <span className="text-fluor-amarillo">{g.bloquesCodelco}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{uf(g.totalUf, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tarjeta>
      )}

      {activos > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filtro.mpa?.map((m) => <Chip key={m} nivel="VERDE">G{m}</Chip>)}
          {filtro.clientes?.map((c) => <Chip key={c} nivel="VERDE">{c}</Chip>)}
          {filtro.centrosCosto?.map((c) => <Chip key={c} nivel="VERDE">{c}</Chip>)}
          {filtro.extras?.map((e) => <Chip key={e} nivel="VERDE">{e}</Chip>)}
        </div>
      )}
    </div>
  );
}
